import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, Globe, UserCircle, LogOut } from 'lucide-react';

function UnreadBadge({ count, active }) {
  if (!count) return null;
  return (
    <span className={`text-xs font-bold rounded-full px-1.5 py-0.5 ${active ? 'bg-white text-red-600' : 'bg-red-500 text-white'}`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function SidebarNav({ items, groupLabels, pathname, unreadCount, pendingApprovals = 0, onNavigate }) {
  return (
    <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
      {items.map((item, idx) => {
        const isActive = pathname === item.href;
        const groupLabel = groupLabels.find(g => g.idx === idx);
        return (
          <React.Fragment key={item.name}>
            {groupLabel && (
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 pt-3 pb-1">{groupLabel.label}</p>
            )}
            <Link
              to={item.href}
              data-testid={`nav-${item.href.substring(1)}`}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium transition-all relative text-sm ${
                isActive ? 'bg-red-600 text-white shadow-lg shadow-red-600/30' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={onNavigate}
            >
              <item.icon className="h-4 w-4" />
              <span className="flex-1">{item.name}</span>
              {item.href === '/messages' && <UnreadBadge count={unreadCount} active={isActive} />}
              {item.href === '/approbations' && <span data-testid="nav-approvals-badge"><UnreadBadge count={pendingApprovals} active={isActive} /></span>}
            </Link>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export function SidebarUserPanel({ user, roleLabel, language, onToggleLanguage, onProfile, onLogout }) {
  return (
    <div className="px-4 py-4 border-t border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-blue-600 flex items-center justify-center text-white font-bold">
            {user?.full_name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-gray-500 truncate">{roleLabel}</p>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onToggleLanguage} className="flex-1" data-testid="language-toggle-btn">
          <Globe className="h-4 w-4 mr-2" />
          {language.toUpperCase()}
        </Button>
        <Button variant="outline" size="sm" onClick={onProfile} data-testid="profile-btn">
          <UserCircle className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={onLogout} data-testid="logout-btn">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function Sidebar({ open, onClose, children }) {
  return (
    <>
      {open && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 bg-white/95 backdrop-blur-xl border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Systeme de Gestion LCS" className="h-12 w-auto" />
            </div>
            <button onClick={onClose} className="lg:hidden p-2 rounded-lg hover:bg-gray-100" data-testid="close-sidebar-btn">
              <X className="h-5 w-5" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </>
  );
}
